import React, { useState, useEffect } from 'react';
import { Camera, Menu, X, Plus, Eye, Download, Share2, Edit, Trash2, LogOut, User, Briefcase, GraduationCap, Code, Mail, Award, FileText, Sparkles } from 'lucide-react';

const PortfolioStudio = () => {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [portfolios, setPortfolios] = useState([]);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Form data for portfolio creation
  const [portfolioData, setPortfolioData] = useState({
    personalInfo: {
      fullName: '',
      title: '',
      email: '',
      phone: '',
      location: '',
      bio: '',
      profileImage: ''
    },
    about: '',
    experience: [],
    projects: [],
    education: [],
    skills: [],
    certifications: [],
    academyProjects: [],
    contact: {
      github: '',
      linkedin: '',
      twitter: '',
      portfolio: ''
    }
  });

  // Mock authentication
  useEffect(() => {
    const mockUser = {
      name: 'Student User',
      email: 'student@example.com',
      photoURL: null,
      uid: 'user123'
    };
    setUser(mockUser);
    loadPortfolios(mockUser.uid);
  }, []);

  const loadPortfolios = (userId) => {
    const saved = localStorage.getItem(`portfolios_${userId}`);
    if (saved) {
      setPortfolios(JSON.parse(saved));
    }
  };

  const savePortfolios = (userId, data) => {
    localStorage.setItem(`portfolios_${userId}`, JSON.stringify(data));
  };

  const handleGoogleSignIn = () => {
    alert('In production, this will connect to Firebase Authentication');
  };

  const handleLogout = () => {
    setUser(null);
    setPortfolios([]);
  };

  const addExperience = () => {
    setPortfolioData({
      ...portfolioData,
      experience: [...portfolioData.experience, {
        id: Date.now(),
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        current: false,
        description: ''
      }]
    });
  };

  const addProject = () => {
    setPortfolioData({
      ...portfolioData,
      projects: [...portfolioData.projects, {
        id: Date.now(),
        title: '',
        description: '',
        technologies: '',
        github: '',
        live: '',
        image: ''
      }]
    });
  };

  const addEducation = () => {
    setPortfolioData({
      ...portfolioData,
      education: [...portfolioData.education, {
        id: Date.now(),
        institution: '',
        degree: '',
        field: '',
        startDate: '',
        endDate: '',
        grade: ''
      }]
    });
  };

  const addSkill = () => {
    const skill = prompt('Enter skill name:');
    if (skill) {
      setPortfolioData({
        ...portfolioData,
        skills: [...portfolioData.skills, { id: Date.now(), name: skill, level: 80 }]
      });
    }
  };

  const generatePortfolio = async () => {
    if (!portfolioData.personalInfo.fullName) {
      alert('Please enter your full name');
      return;
    }

    setGenerating(true);
    
    // Simulate AI generation and deployment
    await new Promise(resolve => setTimeout(resolve, 3000));

    const username = portfolioData.personalInfo.fullName.toLowerCase().replace(/\s+/g, '');
    const newPortfolio = {
      id: Date.now(),
      data: portfolioData,
      createdAt: new Date().toISOString(),
      deploymentUrl: `https://${username}-portfolio.web.app`,
      githubRepo: `https://github.com/geeta-academy/${username}-portfolio`,
      status: 'deployed'
    };

    const updated = [...portfolios, newPortfolio];
    setPortfolios(updated);
    savePortfolios(user.uid, updated);
    setCurrentPortfolio(newPortfolio);
    setGenerating(false);
    setShowPreview(true);
    setActiveSection('dashboard');

    alert('Portfolio generated and deployed successfully!');
  };

  const deletePortfolio = (id) => {
    if (window.confirm('Are you sure you want to delete this portfolio?')) {
      const updated = portfolios.filter(p => p.id !== id);
      setPortfolios(updated);
      savePortfolios(user.uid, updated);
    }
  };

  if (!user) {
    return (
      <div className="min-vh-100  d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-lg" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="card-body text-center p-5">
            <Sparkles size={48} className="text-primary mb-3" />
            <h2 className="mb-3">AI Portfolio Studio</h2>
            <p className="text-muted mb-4">Create professional portfolios powered by AI</p>
            <button onClick={handleGoogleSignIn} className="btn btn-primary btn-lg w-100">
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex vh-100 my-4 py-5 bg-light">
      {/* Sidebar */}
      <div className={`bg-dark text-white ${sidebarOpen ? '' : 'd-none'}`} style={{ width: '280px', transition: 'all 0.3s' }}>
        <div className="p-3 border-bottom border-secondary">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0 d-flex align-items-center">
              <Sparkles size={20} className="me-2" />
              Portfolio Studio
            </h5>
            <button onClick={() => setSidebarOpen(false)} className="btn btn-sm btn-dark d-md-none">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-3">
          <button 
            onClick={() => setActiveSection('create')}
            className="btn btn-primary w-100 mb-3 d-flex align-items-center justify-content-center"
          >
            <Plus size={18} className="me-2" />
            Create Portfolio
          </button>

          <div className="list-group list-group-flush">
            <button
              onClick={() => setActiveSection('dashboard')}
              className={`list-group-item list-group-item-action bg-dark text-white border-0 ${activeSection === 'dashboard' ? 'active' : ''}`}
            >
              <FileText size={18} className="me-2" />
              My Portfolios
            </button>
            <button
              onClick={() => setActiveSection('templates')}
              className={`list-group-item list-group-item-action bg-dark text-white border-0 ${activeSection === 'templates' ? 'active' : ''}`}
            >
              <Camera size={18} className="me-2" />
              Templates
            </button>
            <button
              onClick={() => setActiveSection('analytics')}
              className={`list-group-item list-group-item-action bg-dark text-white border-0 ${activeSection === 'analytics' ? 'active' : ''}`}
            >
              <Award size={18} className="me-2" />
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 d-flex flex-column">
        {/* Header */}
        <nav className="navbar navbar-light bg-white border-bottom px-3">
          <div className="d-flex align-items-center w-100 justify-content-between">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="btn btn-light">
              <Menu size={20} />
            </button>
            
            <div className="dropdown">
              <button className="btn btn-light dropdown-toggle d-flex align-items-center" type="button" data-bs-toggle="dropdown">
                <div className="bg-primary text-white rounded-circle me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                  {user.name.charAt(0)}
                </div>
                {user.name}
              </button>
              <ul className="dropdown-menu">
                <li><button className="dropdown-item" onClick={() => setActiveSection('profile')}><User size={16} className="me-2" />Profile</button></li>
                <li><hr className="dropdown-divider" /></li>
                <li><button className="dropdown-item" onClick={handleLogout}><LogOut size={16} className="me-2" />Logout</button></li>
              </ul>
            </div>
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-grow-1 overflow-auto p-4">
          {activeSection === 'dashboard' && (
            <div>
              <h2 className="mb-4">My Portfolios</h2>
              {portfolios.length === 0 ? (
                <div className="text-center py-5">
                  <Sparkles size={64} className="text-muted mb-3" />
                  <h4 className="text-muted">No portfolios yet</h4>
                  <p className="text-muted">Create your first AI-powered portfolio</p>
                  <button onClick={() => setActiveSection('create')} className="btn btn-primary">
                    Create Portfolio
                  </button>
                </div>
              ) : (
                <div className="row g-4">
                  {portfolios.map(portfolio => (
                    <div key={portfolio.id} className="col-md-6 col-lg-4">
                      <div className="card h-100 shadow-sm">
                        <div className="card-body">
                          <h5 className="card-title">{portfolio.data.personalInfo.fullName}</h5>
                          <p className="card-text text-muted small">{portfolio.data.personalInfo.title}</p>
                          <div className="mb-3">
                            <span className="badge bg-success">Deployed</span>
                          </div>
                          <div className="d-flex gap-2">
                            <button 
                              onClick={() => window.open(portfolio.deploymentUrl, '_blank')}
                              className="btn btn-sm btn-primary flex-grow-1"
                            >
                              <Eye size={16} className="me-1" />
                              View
                            </button>
                            <button 
                              onClick={() => window.open(portfolio.githubRepo, '_blank')}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <Code size={16} />
                            </button>
                            <button 
                              onClick={() => deletePortfolio(portfolio.id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="card-footer bg-light">
                          <small className="text-muted">
                            Created: {new Date(portfolio.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeSection === 'create' && (
            <div className="container-fluid">
              <h2 className="mb-4">Create Portfolio</h2>
              
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <User size={20} className="me-2" />
                  Personal Information
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Full Name *</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={portfolioData.personalInfo.fullName}
                        onChange={e => setPortfolioData({
                          ...portfolioData,
                          personalInfo: { ...portfolioData.personalInfo, fullName: e.target.value }
                        })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Professional Title</label>
                      <input 
                        type="text" 
                        className="form-control"
                        placeholder="e.g., Full Stack Developer"
                        value={portfolioData.personalInfo.title}
                        onChange={e => setPortfolioData({
                          ...portfolioData,
                          personalInfo: { ...portfolioData.personalInfo, title: e.target.value }
                        })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input 
                        type="email" 
                        className="form-control"
                        value={portfolioData.personalInfo.email}
                        onChange={e => setPortfolioData({
                          ...portfolioData,
                          personalInfo: { ...portfolioData.personalInfo, email: e.target.value }
                        })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input 
                        type="tel" 
                        className="form-control"
                        value={portfolioData.personalInfo.phone}
                        onChange={e => setPortfolioData({
                          ...portfolioData,
                          personalInfo: { ...portfolioData.personalInfo, phone: e.target.value }
                        })}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Bio</label>
                      <textarea 
                        className="form-control" 
                        rows="3"
                        placeholder="Tell us about yourself..."
                        value={portfolioData.personalInfo.bio}
                        onChange={e => setPortfolioData({
                          ...portfolioData,
                          personalInfo: { ...portfolioData.personalInfo, bio: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <span><Briefcase size={20} className="me-2" />Experience</span>
                  <button onClick={addExperience} className="btn btn-sm btn-light">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="card-body">
                  {portfolioData.experience.length === 0 ? (
                    <p className="text-muted">No experience added yet</p>
                  ) : (
                    portfolioData.experience.map((exp, idx) => (
                      <div key={exp.id} className="border rounded p-3 mb-3">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Company"
                              value={exp.company}
                              onChange={e => {
                                const updated = [...portfolioData.experience];
                                updated[idx].company = e.target.value;
                                setPortfolioData({ ...portfolioData, experience: updated });
                              }}
                            />
                          </div>
                          <div className="col-md-6">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Position"
                              value={exp.position}
                              onChange={e => {
                                const updated = [...portfolioData.experience];
                                updated[idx].position = e.target.value;
                                setPortfolioData({ ...portfolioData, experience: updated });
                              }}
                            />
                          </div>
                          <div className="col-12">
                            <textarea 
                              className="form-control" 
                              rows="2" 
                              placeholder="Description"
                              value={exp.description}
                              onChange={e => {
                                const updated = [...portfolioData.experience];
                                updated[idx].description = e.target.value;
                                setPortfolioData({ ...portfolioData, experience: updated });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <span><Code size={20} className="me-2" />Projects</span>
                  <button onClick={addProject} className="btn btn-sm btn-light">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="card-body">
                  {portfolioData.projects.length === 0 ? (
                    <p className="text-muted">No projects added yet</p>
                  ) : (
                    portfolioData.projects.map((proj, idx) => (
                      <div key={proj.id} className="border rounded p-3 mb-3">
                        <div className="row g-3">
                          <div className="col-12">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Project Title"
                              value={proj.title}
                              onChange={e => {
                                const updated = [...portfolioData.projects];
                                updated[idx].title = e.target.value;
                                setPortfolioData({ ...portfolioData, projects: updated });
                              }}
                            />
                          </div>
                          <div className="col-12">
                            <textarea 
                              className="form-control" 
                              rows="2" 
                              placeholder="Description"
                              value={proj.description}
                              onChange={e => {
                                const updated = [...portfolioData.projects];
                                updated[idx].description = e.target.value;
                                setPortfolioData({ ...portfolioData, projects: updated });
                              }}
                            />
                          </div>
                          <div className="col-md-6">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Technologies (comma separated)"
                              value={proj.technologies}
                              onChange={e => {
                                const updated = [...portfolioData.projects];
                                updated[idx].technologies = e.target.value;
                                setPortfolioData({ ...portfolioData, projects: updated });
                              }}
                            />
                          </div>
                          <div className="col-md-6">
                            <input 
                              type="url" 
                              className="form-control" 
                              placeholder="GitHub URL"
                              value={proj.github}
                              onChange={e => {
                                const updated = [...portfolioData.projects];
                                updated[idx].github = e.target.value;
                                setPortfolioData({ ...portfolioData, projects: updated });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <span><GraduationCap size={20} className="me-2" />Education</span>
                  <button onClick={addEducation} className="btn btn-sm btn-light">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="card-body">
                  {portfolioData.education.length === 0 ? (
                    <p className="text-muted">No education added yet</p>
                  ) : (
                    portfolioData.education.map((edu, idx) => (
                      <div key={edu.id} className="border rounded p-3 mb-3">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Institution"
                              value={edu.institution}
                              onChange={e => {
                                const updated = [...portfolioData.education];
                                updated[idx].institution = e.target.value;
                                setPortfolioData({ ...portfolioData, education: updated });
                              }}
                            />
                          </div>
                          <div className="col-md-6">
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Degree"
                              value={edu.degree}
                              onChange={e => {
                                const updated = [...portfolioData.education];
                                updated[idx].degree = e.target.value;
                                setPortfolioData({ ...portfolioData, education: updated });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <span><Award size={20} className="me-2" />Skills</span>
                  <button onClick={addSkill} className="btn btn-sm btn-light">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    {portfolioData.skills.map(skill => (
                      <span key={skill.id} className="badge bg-primary p-2">
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button 
                  onClick={generatePortfolio}
                  disabled={generating}
                  className="btn btn-primary btn-lg px-5"
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Generating & Deploying...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} className="me-2" />
                      Generate Portfolio
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {activeSection === 'templates' && (
            <div>
              <h2 className="mb-4">Portfolio Templates</h2>
              <div className="row g-4">
                {['Modern', 'Minimal', 'Creative', 'Professional'].map(template => (
                  <div key={template} className="col-md-6 col-lg-3">
                    <div className="card h-100">
                      <div className="bg-secondary" style={{ height: '200px' }}></div>
                      <div className="card-body">
                        <h5 className="card-title">{template}</h5>
                        <button className="btn btn-sm btn-primary">Use Template</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'analytics' && (
            <div>
              <h2 className="mb-4">Analytics</h2>
              <div className="row g-4">
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h3 className="text-primary">{portfolios.length}</h3>
                      <p className="text-muted mb-0">Total Portfolios</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h3 className="text-success">{portfolios.filter(p => p.status === 'deployed').length}</h3>
                      <p className="text-muted mb-0">Deployed</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body text-center">
                      <h3 className="text-info">0</h3>
                      <p className="text-muted mb-0">Total Views</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioStudio;